import { cn } from '@/lib/utils';
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';

interface SidebarResizeHandleProps {
  side: 'left' | 'right';
  label: string;
  width: number;
  onWidthChange: (width: number) => void;
  collapsed?: boolean;
  collapsedWidth?: number;
  snapPoints?: readonly number[];
  onCollapsedChange: (collapsed: boolean) => void;
}

const MIN_WIDTH = 200;
const MAX_WIDTH = 520;
const COLLAPSE_WIDTH = 160;
const SNAP_DISTANCE = 12;

export function SidebarResizeHandle({
  side,
  label,
  width,
  onWidthChange,
  collapsed = false,
  collapsedWidth = 0,
  snapPoints = [],
  onCollapsedChange,
}: SidebarResizeHandleProps) {
  const [resizing, setResizing] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const callbacksRef = useRef({ onWidthChange, onCollapsedChange });

  useEffect(() => {
    callbacksRef.current = { onWidthChange, onCollapsedChange };
  }, [onWidthChange, onCollapsedChange]);

  useEffect(() => () => cleanupRef.current?.(), []);

  const resize = (nextWidth: number) => {
    const callbacks = callbacksRef.current;
    if (nextWidth < COLLAPSE_WIDTH) {
      callbacks.onCollapsedChange(true);
      return;
    }
    const clampedWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, nextWidth));
    const snapPoint = snapPoints.reduce<number | undefined>((closest, point) => {
      if (point < MIN_WIDTH || point > MAX_WIDTH || Math.abs(point - clampedWidth) > SNAP_DISTANCE) return closest;
      return closest === undefined || Math.abs(point - clampedWidth) < Math.abs(closest - clampedWidth) ? point : closest;
    }, undefined);
    callbacks.onWidthChange(Math.round(snapPoint ?? clampedWidth));
    callbacks.onCollapsedChange(false);
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.isPrimary === false) return;
    event.preventDefault();
    event.stopPropagation();
    cleanupRef.current?.();
    const handle = event.currentTarget;
    handle.focus();
    const startX = event.clientX;
    const startWidth = handle.parentElement?.getBoundingClientRect().width || (collapsed ? collapsedWidth : width);
    const pointerId = event.pointerId;
    const direction = side === 'left' ? 1 : -1;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    setResizing(true);

    const cleanup = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', pointerCancel, true);
      window.removeEventListener('keydown', key, true);
      window.removeEventListener('blur', cancel);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      cleanupRef.current = null;
    };
    const finish = () => {
      cleanup();
      setResizing(false);
    };
    const move = (next: PointerEvent) => {
      if (next.pointerId !== pointerId) return;
      next.preventDefault();
      next.stopPropagation();
      resize(startWidth + direction * (next.clientX - startX));
    };
    const up = (next: PointerEvent) => {
      if (next.pointerId !== pointerId) return;
      next.stopPropagation();
      finish();
    };
    const cancel = () => {
      callbacksRef.current.onWidthChange(width);
      callbacksRef.current.onCollapsedChange(collapsed);
      finish();
    };
    const pointerCancel = (next: PointerEvent) => {
      if (next.pointerId === pointerId) cancel();
    };
    const key = (next: globalThis.KeyboardEvent) => {
      if (next.key !== 'Escape') return;
      next.preventDefault();
      next.stopPropagation();
      cancel();
    };

    cleanupRef.current = cleanup;
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', pointerCancel, true);
    window.addEventListener('keydown', key, true);
    window.addEventListener('blur', cancel);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const direction = side === 'left' ? 1 : -1;
    const delta = event.key === 'ArrowLeft' ? -direction : event.key === 'ArrowRight' ? direction : 0;
    if (!delta && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'Home' || (!collapsed && delta < 0 && width <= MIN_WIDTH)) {
      onCollapsedChange(true);
    } else if (event.key === 'End') {
      resize(MAX_WIDTH);
    } else if (collapsed) {
      if (delta > 0) onCollapsedChange(false);
    } else {
      resize(width + delta * (event.shiftKey ? 40 : 20));
    }
  };

  return (
    <div
      role='separator'
      aria-label={label}
      aria-orientation='vertical'
      aria-valuemin={collapsedWidth}
      aria-valuemax={MAX_WIDTH}
      aria-valuenow={collapsed ? collapsedWidth : width}
      tabIndex={0}
      title={label}
      onPointerDown={onPointerDown}
      onDoubleClick={(event) => {
        const defaultWidth = snapPoints[0];
        if (defaultWidth === undefined) return;
        event.preventDefault();
        event.stopPropagation();
        cleanupRef.current?.();
        setResizing(false);
        resize(defaultWidth);
      }}
      onKeyDown={onKeyDown}
      className={cn(
        'absolute inset-y-0 z-30 w-2 cursor-col-resize touch-none select-none focus-visible:outline-none after:absolute after:inset-y-0 after:w-0.5 hover:after:bg-sky-500',
        side === 'left' ? 'right-0 after:right-0' : 'left-0 after:left-0',
        resizing && 'after:bg-sky-500',
      )}
    />
  );
}
